import React, { useState, useEffect, useRef } from 'react';
import {
  OrderOutlet,
  CartItem,
  MenuItem,
  CustomerOrder,
  OrderType,
  PaymentMethod,
  AddOnOption,
  CustomerProfile,
  SelectedCustomOption,
  SpecialForYouItem,
} from '../../../types';
import { OutletSelector } from './OutletSelector';
import { OrderMenu } from './OrderMenu';
import { OrderCartDrawer } from './OrderCartDrawer';
import { OrderCheckout } from './OrderCheckout';
import { OrderConfirmation } from './OrderConfirmation';
import CustomerAuthForm from './CustomerAuthForm';
import CustomerProfileTab from './CustomerProfileTab';
import { CustomerHeader } from '../../common/CustomerHeader';
import { createNewOrder, generateOrderNumber, fetchSingleOrder } from '../../../utils/supabaseOrders';
import { findOrCreateCustomerMember } from '../../../utils/supabaseCustomers';
import { getCurrentCustomerProfile, getSupabase, logoutCustomer } from '../../../utils/supabase';
import { getCustomerLoyalty } from '../../../utils/supabaseLoyalty';
import { resolveMediaUrl } from '../../../utils/api';
import { DEFAULT_OUTLETS } from '../../../data/outletsData';
import { initialLetonData } from '../../../data/initialData';
import { isMenuItemAvailableForOutlet } from '../../../utils/supabaseStock';
import { useContent } from '../../../context/ContentContext';
import {
  DEFAULT_SIZE,
  DEFAULT_TOPPING,
  DEFAULT_SYRUP,
  generateCartItemId,
  calculateItemUnitPrice,
} from '../../../data/addOnsData';
import { FIXED_SERVICE_FEE } from '../../../utils/deliveryFee';
import { X, ArrowLeft, ArrowRight, ShoppingBag, User, UserPlus, LogIn, UserCheck, Award, Coffee } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import { ErrorBoundary } from '../../common/ErrorBoundary';

function resolvePromoCatalogProduct(
  promo: SpecialForYouItem,
  menuCatalog: MenuItem[],
  outletId?: string | null
): MenuItem | null {
  const catalog = Array.isArray(menuCatalog) && menuCatalog.length > 0
    ? menuCatalog
    : initialLetonData.menuItems;
  if (!catalog || catalog.length === 0) return null;

  // 1. Direct reference via promo.product_id
  if (promo.product_id) {
    const byId = catalog.find((m) => m.id === promo.product_id);
    if (byId) return byId;
  }

  // 2. Match against default promo mapping in initialLetonData.specialForYou
  const defaultPromo = initialLetonData.specialForYou.find((p) => p.id === promo.id);
  if (defaultPromo?.product_id) {
    const byDefaultId = catalog.find((m) => m.id === defaultPromo.product_id);
    if (byDefaultId) return byDefaultId;
  }

  // 3. Match by exact or partial title/description against real catalog product names
  const hay = `${promo.title} ${promo.description || ''}`.toLowerCase();
  const byName = catalog.find(
    (m) =>
      hay.includes(m.name.toLowerCase()) &&
      (!outletId || isMenuItemAvailableForOutlet(m, outletId))
  );
  if (byName) return byName;

  // 4. Match by price in real catalog (preferring available at outlet)
  if (typeof promo.price === 'number' && promo.price > 0) {
    const byPriceAvailable = catalog.find(
      (m) => m.price === promo.price && (!outletId || isMenuItemAvailableForOutlet(m, outletId))
    );
    if (byPriceAvailable) return byPriceAvailable;
  }

  // 5. Fallback to first available catalog item at the outlet (or first catalog item)
  return (
    catalog.find((m) => !outletId || isMenuItemAvailableForOutlet(m, outletId)) ||
    catalog[0] ||
    null
  );
}

function buildPromoCartItem(promo: SpecialForYouItem, product: MenuItem): CartItem {
  const size = product.hasSize !== false ? DEFAULT_SIZE : undefined;
  const topping = DEFAULT_TOPPING;
  const syrup = DEFAULT_SYRUP;
  const note = promo.discount_info
    ? `Promo ${promo.title} (${promo.discount_info})`
    : `Promo: ${promo.title}`;
  return {
    id: generateCartItemId(product.id, size?.name, topping.name, syrup.name),
    product,
    quantity: 1,
    size,
    topping,
    syrup,
    note,
  };
}

interface OrderingSystemModalProps {
  isOpen: boolean;
  onClose: () => void;
  preSelectedMenuItem?: MenuItem | null;
  isMemberOnlyFlow?: boolean;
  initialOutletId?: string | null;
  initialOrderType?: OrderType;
  initialPromoItem?: SpecialForYouItem | null;
  onBackToPromoDetail?: () => void;
}

function getOutletIdFromUrl(): 'sudirman' | 'kelakap_7' | null {
  if (typeof window === 'undefined') return null;
  try {
    const searchParams = new URLSearchParams(window.location.search);
    let raw = searchParams.get('outlet');
    if (!raw && window.location.hash.includes('?')) {
      const hashQuery = window.location.hash.split('?')[1] || '';
      raw = new URLSearchParams(hashQuery).get('outlet');
    }
    if (!raw) return null;
    const clean = raw.trim().toLowerCase();
    if (clean === 'sudirman') return 'sudirman';
    if (clean === 'kelakap_7') return 'kelakap_7';
    return null;
  } catch {
    return null;
  }
}

function resolveQrOutlet(outletId: string | null | undefined, branches?: any[], whatsapp?: string): OrderOutlet | null {
  if (!outletId) return null;
  const clean = outletId.trim().toLowerCase();
  if (clean !== 'sudirman' && clean !== 'kelakap_7') return null;

  const baseOutlet = DEFAULT_OUTLETS.find((o) => o.id === clean);
  if (!baseOutlet) return null;

  let dynamicImage = baseOutlet.image;
  let dynamicAddress = baseOutlet.address;
  let dynamicHours = baseOutlet.hours;

  if (clean === 'sudirman') {
    const b5 = branches?.find((b) => b.id === 'chapter-5') || branches?.[0];
    if (b5) {
      dynamicImage = b5.bgImage || baseOutlet.image;
      dynamicAddress = b5.address || baseOutlet.address;
      dynamicHours = b5.openingHours || baseOutlet.hours;
    }
  } else if (clean === 'kelakap_7') {
    const b6 = branches?.find((b) => b.id === 'chapter-6') || branches?.[1];
    if (b6) {
      dynamicImage = b6.bgImage || baseOutlet.image;
      dynamicAddress = b6.address || baseOutlet.address;
      dynamicHours = b6.openingHours || baseOutlet.hours;
    }
  }

  return {
    ...baseOutlet,
    image: dynamicImage,
    address: dynamicAddress,
    hours: dynamicHours,
    whatsapp: whatsapp || baseOutlet.whatsapp,
  };
}

export const OrderingSystemModal: React.FC<OrderingSystemModalProps> = ({
  isOpen,
  onClose,
  preSelectedMenuItem,
  isMemberOnlyFlow = false,
  initialOutletId = null,
  initialOrderType = 'TAKE AWAY',
  initialPromoItem = null,
  onBackToPromoDetail,
}) => {
  const { data } = useContent();
  const siteSettings = data?.siteSettings;
  const cmsLogoSrc = siteSettings?.logoUrl
    ? resolveMediaUrl(siteSettings.logoUrl)
    : '/logo_icon.jpg';
  const [logoImgError, setLogoImgError] = useState(false);

  useEffect(() => {
    setLogoImgError(false);
  }, [siteSettings?.logoUrl]);

  // Order mode state (Pick Up vs Delivery), adaptive from entry point
  const [activeOrderType, setActiveOrderType] = useState<OrderType>(() => initialOrderType || 'TAKE AWAY');

  useEffect(() => {
    if (isOpen) {
      setActiveOrderType(initialOrderType || 'TAKE AWAY');
    }
  }, [isOpen, initialOrderType]);

  // Initialize selectedOutlet from QR deep link if valid, otherwise null for a normal ordering session
  const [selectedOutlet, setSelectedOutlet] = useState<OrderOutlet | null>(() => {
    if (initialPromoItem) return null;
    const targetId = initialOutletId || getOutletIdFromUrl();
    return resolveQrOutlet(targetId, data?.branches, data?.contactSettings?.whatsapp);
  });

  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem('leton_ordering_cart');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed.filter((item) => item && item.product && typeof item.product.id === 'string');
        }
      }
    } catch {
      return [];
    }
    return [];
  });

  const [isCartOpen, setIsCartOpen] = useState<boolean>(false);
  const [generalNote, setGeneralNote] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<
    'member_choice' | 'outlet' | 'menu' | 'checkout' | 'confirmation' | 'profile'
  >(() => {
    if (initialPromoItem) {
      return 'outlet';
    }
    const targetId = initialOutletId || getOutletIdFromUrl();
    const qrOutlet = resolveQrOutlet(targetId, data?.branches, data?.contactSettings?.whatsapp);
    if (!isMemberOnlyFlow && qrOutlet) {
      return 'menu';
    }
    return 'member_choice';
  });
  const [memberChoiceStep, setMemberChoiceStep] = useState<'choice' | 'register' | 'login'>('choice');
  const [completedOrder, setCompletedOrder] = useState<CustomerOrder | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [pendingCustomizeItem, setPendingCustomizeItem] = useState<MenuItem | null>(null);
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null);
  const [checkingAuth, setCheckingAuth] = useState<boolean>(() => {
    const targetId = initialOutletId || getOutletIdFromUrl();
    return !(!isMemberOnlyFlow && resolveQrOutlet(targetId));
  });
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const isSubmittingOrderRef = useRef<boolean>(false);
  const pendingOrderDraftRef = useRef<{
    fingerprint: string;
    id: string;
    orderNumber: string;
  } | null>(null);

  // Sync active customer profile session on mount and when modal opens
  useEffect(() => {
    let isEffectActive = true;
    if (isOpen) {
      // Deteksi redirect kembali dari DANA Hosted Checkout: #order?orderId=...&source=dana_return
      const checkDanaReturn = (): boolean => {
        if (typeof window === 'undefined') return false;
        const hash = window.location.hash || '';
        const hashSearch = hash.includes('?') ? hash.split('?')[1] : '';
        const urlParams = new URLSearchParams(hashSearch || window.location.search || '');
        const danaReturnOrderId = (urlParams.get('orderId') || urlParams.get('orderNumber') || '').trim();
        const isDanaReturn = urlParams.get('source') === 'dana_return';

        if (!isDanaReturn || !danaReturnOrderId) return false;

        // Bersihkan query parameter return DANA agar tidak merusak hash routing selanjutnya
        try {
          window.history.replaceState(null, '', `${window.location.pathname}#order`);
        } catch {}

        let cachedOrder: CustomerOrder | null = null;
        try {
          const rawCached =
            sessionStorage.getItem('leton_dana_pending_order') ||
            localStorage.getItem('leton_dana_pending_order');
          if (rawCached) {
            const parsed = JSON.parse(rawCached);
            if (
              parsed &&
              (parsed.id === danaReturnOrderId ||
                parsed.orderNumber === danaReturnOrderId ||
                String(parsed.orderNumber || '').replace(/^#/, '') === danaReturnOrderId.replace(/^#/, ''))
            ) {
              // KEAMANAN: Pemulihan dari cache hanya memulihkan tampilan dalam status WAITING PAYMENT,
              // bukan menandai pembayaran berhasil. Status PAID hanya boleh berasal dari server/database.
              cachedOrder = {
                ...parsed,
                paymentMethod: 'DANA',
                paymentStatus: 'WAITING PAYMENT',
                orderStatus: parsed.orderStatus === 'CANCELLED' ? 'CANCELLED' : 'NEW',
              };
            }
          }
        } catch {}

        const resolvedPhone =
          cachedOrder?.customerPhone ||
          customerProfile?.nomorHp ||
          (() => {
            try {
              const rawGuest = localStorage.getItem('leton_guest_checkout_info');
              return rawGuest ? JSON.parse(rawGuest)?.phone || '' : '';
            } catch {
              return '';
            }
          })();

        if (cachedOrder) {
          setCompletedOrder(cachedOrder);
          setCurrentStep('confirmation');
          setCart([]);
          localStorage.removeItem('leton_ordering_cart');
          if (cachedOrder.outletId) {
            const matched = resolveQrOutlet(cachedOrder.outletId, data?.branches, data?.contactSettings?.whatsapp);
            if (matched) setSelectedOutlet(matched);
          }
          setCheckingAuth(false);
        } else {
          setCheckingAuth(true);
        }

        fetchSingleOrder(danaReturnOrderId, resolvedPhone)
          .then((foundOrder) => {
            if (!isEffectActive) return;
            const targetOrder: CustomerOrder =
              foundOrder ||
              cachedOrder || {
                id: danaReturnOrderId,
                orderNumber: danaReturnOrderId,
                outletId: selectedOutlet?.id || 'sudirman',
                outletName: selectedOutlet?.name || 'LetonKu',
                customerName: customerProfile?.namaLengkap || 'Pelanggan',
                customerPhone: resolvedPhone,
                orderType: 'TAKE AWAY',
                items: [],
                totalAmount: 0,
                paymentMethod: 'DANA',
                paymentStatus: 'WAITING PAYMENT',
                orderStatus: 'NEW',
                createdAt: new Date().toISOString(),
              };

            if (foundOrder && (foundOrder.paymentStatus === 'PAID' || foundOrder.paymentStatus === 'PAYMENT REJECTED')) {
              try {
                sessionStorage.removeItem('leton_dana_pending_order');
                localStorage.removeItem('leton_dana_pending_order');
              } catch {}
            }

            setCompletedOrder(targetOrder);
            setCurrentStep('confirmation');
            setCart([]);
            if (typeof window !== 'undefined') {
              localStorage.removeItem('leton_ordering_cart');
            }
            if (targetOrder.outletId) {
              const matched = resolveQrOutlet(targetOrder.outletId, data?.branches, data?.contactSettings?.whatsapp);
              if (matched) setSelectedOutlet(matched);
            }
          })
          .catch((err) => {
            console.warn('[DANA Return] Order fetch error:', err);
          })
          .finally(() => {
            if (isEffectActive) {
              setCheckingAuth(false);
            }
          });
        return true;
      };

      if (checkDanaReturn()) {
        return () => {
          isEffectActive = false;
        };
      }

      const activeDeepLinkId = initialOutletId || getOutletIdFromUrl();
      const qrOutlet = !isMemberOnlyFlow
        ? resolveQrOutlet(activeDeepLinkId, data?.branches, data?.contactSettings?.whatsapp)
        : null;

      if (initialPromoItem) {
        // PROMO FLOW: Always start on Outlet Selector with real catalog product pre-loaded in cart
        setSelectedOutlet(null);
        setCurrentStep('outlet');
        setCheckingAuth(false);

        const catalog =
          Array.isArray(data?.menuItems) && data.menuItems.length > 0
            ? data.menuItems
            : initialLetonData.menuItems;
        const matchedProduct = resolvePromoCatalogProduct(initialPromoItem, catalog, null);

        if (matchedProduct) {
          const promoCartItem = buildPromoCartItem(initialPromoItem, matchedProduct);
          setCart([promoCartItem]);
          try {
            localStorage.setItem('leton_ordering_cart', JSON.stringify([promoCartItem]));
          } catch {}
        }

        if (initialPromoItem.id.toLowerCase().includes('delivery')) {
          setActiveOrderType('DELIVERY');
        }
      } else if (qrOutlet) {
        // QR Deep-Link Flow: immediately select outlet and open menu catalog
        setSelectedOutlet(qrOutlet);
        setCurrentStep('menu');
        setCheckingAuth(false);
      } else {
        setCheckingAuth(true);
        // STRICT REQUIREMENT: Always reset selectedOutlet on every normal ORDER ONLINE session
        setSelectedOutlet(null);
      }

      const syncProfile = async () => {
        try {
          const profile = await getCurrentCustomerProfile();
          setCustomerProfile(profile);
          if (initialPromoItem) {
            // Promo Flow: Always stay on 'outlet' step regardless of member status
            setCurrentStep('outlet');
            return;
          }
          if (isMemberOnlyFlow) {
            if (profile) {
              setCurrentStep('profile');
            } else {
              setCurrentStep('member_choice');
              setMemberChoiceStep('choice');
            }
          } else if (qrOutlet) {
            setSelectedOutlet((prev) => prev || qrOutlet);
            setCurrentStep('menu');
          } else {
            if (profile) {
              // Logged in as member -> ALWAYS proceed to 'outlet' (Pilih Outlet), NEVER auto-skip to menu!
              setCurrentStep('outlet');
            } else {
              // Not logged in -> show HALAMAN MEMBER choice screen
              setCurrentStep('member_choice');
              setMemberChoiceStep('choice');
            }
          }
        } catch (err) {
          console.warn('Error syncing customer profile:', err);
          setCustomerProfile(null);
          if (initialPromoItem) {
            setCurrentStep('outlet');
          } else if (!qrOutlet) {
            setCurrentStep('member_choice');
            setMemberChoiceStep('choice');
          }
        } finally {
          setCheckingAuth(false);
        }
      };
      syncProfile();

      const handleReturnNav = () => {
        checkDanaReturn();
      };
      window.addEventListener('hashchange', handleReturnNav);
      window.addEventListener('popstate', handleReturnNav);
      return () => {
        isEffectActive = false;
        window.removeEventListener('hashchange', handleReturnNav);
        window.removeEventListener('popstate', handleReturnNav);
      };
    }
  }, [isOpen, isMemberOnlyFlow, initialOutletId, initialPromoItem]);

  // Keep selectedOutlet metadata synced with dynamic branch data
  useEffect(() => {
    if (!selectedOutlet) return;
    const updated = resolveQrOutlet(selectedOutlet.id, data?.branches, data?.contactSettings?.whatsapp);
    if (
      updated &&
      (updated.address !== selectedOutlet.address ||
        updated.hours !== selectedOutlet.hours ||
        updated.image !== selectedOutlet.image ||
        updated.whatsapp !== selectedOutlet.whatsapp)
    ) {
      setSelectedOutlet((prev) =>
        prev
          ? {
              ...prev,
              image: updated.image,
              address: updated.address,
              hours: updated.hours,
              whatsapp: updated.whatsapp,
            }
          : prev
      );
    }
  }, [data?.branches, data?.contactSettings?.whatsapp, selectedOutlet]);

  // Scroll reset helper for modal and window
  const scrollToTop = () => {
    try {
      if (modalContainerRef.current) {
        modalContainerRef.current.scrollTop = 0;
        if (typeof modalContainerRef.current.scrollTo === 'function') {
          modalContainerRef.current.scrollTo({ top: 0, left: 0 });
        }
      }
    } catch (e) {
      console.warn('Scroll modalContainerRef failed:', e);
    }

    try {
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, left: 0 });
      }
    } catch (e) {
      console.warn('Scroll window failed:', e);
    }

    try {
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
      if (document.body) {
        document.body.scrollTop = 0;
      }
    } catch (e) {
      console.warn('Scroll document bodies failed:', e);
    }
  };

  // Automatically reset scroll whenever step changes, especially upon entering confirmation
  useEffect(() => {
    scrollToTop();
    const rafId = requestAnimationFrame(() => {
      scrollToTop();
    });
    const t1 = setTimeout(scrollToTop, 20);
    const t2 = setTimeout(scrollToTop, 100);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [currentStep]);

  // Sync cart to localStorage
  useEffect(() => {
    localStorage.setItem('leton_ordering_cart', JSON.stringify(cart));
  }, [cart]);

  // Sync outlet to localStorage
  useEffect(() => {
    if (selectedOutlet) {
      localStorage.setItem('leton_selected_outlet', JSON.stringify(selectedOutlet));
    }
  }, [selectedOutlet]);

  // If a menu item was clicked from the public page, set it to customize
  useEffect(() => {
    if (isOpen && preSelectedMenuItem) {
      setPendingCustomizeItem(preSelectedMenuItem);
    }
  }, [isOpen, preSelectedMenuItem]);

  if (!isOpen) return null;

  // Cart operations with Add-ons
  const handleAddToCart = (
    product: MenuItem,
    size: AddOnOption = DEFAULT_SIZE,
    topping: AddOnOption = DEFAULT_TOPPING,
    syrup: AddOnOption = DEFAULT_SYRUP,
    quantity: number = 1,
    note?: string,
    customOptions?: SelectedCustomOption[]
  ) => {
    const cartItemId = generateCartItemId(product.id, size.name, topping.name, syrup.name, customOptions);
    setCart((prev) => {
      const existingIndex = prev.findIndex(
        (item) =>
          (item.id ||
            generateCartItemId(
              item.product.id,
              item.size?.name,
              item.topping?.name,
              item.syrup?.name,
              item.customOptions
            )) === cartItemId
      );
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity,
          note: note !== undefined ? note : updated[existingIndex].note,
        };
        return updated;
      } else {
        return [
          ...prev,
          {
            id: cartItemId,
            product,
            quantity,
            size,
            topping,
            syrup,
            customOptions,
            note,
          },
        ];
      }
    });
  };

  const handleUpdateQuantity = (cartItemId: string, delta: number) => {
    setCart((prev) => {
      return prev
        .map((item) => {
          const id =
            item.id ||
            generateCartItemId(
              item.product.id,
              item.size?.name,
              item.topping?.name,
              item.syrup?.name,
              item.customOptions
            );
          if (id === cartItemId || item.product.id === cartItemId) {
            const newQty = item.quantity + delta;
            return newQty > 0 ? { ...item, quantity: newQty } : null;
          }
          return item;
        })
        .filter(Boolean) as CartItem[];
    });
  };

  const handleRemoveItem = (cartItemId: string) => {
    setCart((prev) =>
      prev.filter((item) => {
        const id =
          item.id ||
          generateCartItemId(
            item.product.id,
            item.size?.name,
            item.topping?.name,
            item.syrup?.name,
            item.customOptions
          );
        return id !== cartItemId && item.product.id !== cartItemId;
      })
    );
  };

  const handleUpdateNote = (cartItemId: string, note: string) => {
    setCart((prev) =>
      prev.map((item) => {
        const id =
          item.id ||
          generateCartItemId(
            item.product.id,
            item.size?.name,
            item.topping?.name,
            item.syrup?.name,
            item.customOptions
          );
        return id === cartItemId || item.product.id === cartItemId ? { ...item, note } : item;
      })
    );
  };

  // Submit Order logic
  const handleSubmitOrder = async (details: {
    customerName: string;
    customerPhone: string;
    orderType: OrderType;
    tableNumber: string;
    paymentMethod: PaymentMethod;
    paymentReceiptUrl?: string;
    paymentReceiptPath?: string;
    isMemberChoice?: boolean;
    pickupTime?: string;
    voucherId?: string;
    voucherCode?: string;
    discountAmount?: number;
    deliveryAddress?: string;
    customerLat?: number;
    customerLng?: number;
    deliveryDistance?: number;
    deliveryFee?: number;
  }) => {
    if (!selectedOutlet || isSubmitting || isSubmittingOrderRef.current) {
      console.warn('[ORDER UI] submit ignored: already submitting or no outlet selected');
      return { success: false, error: 'Pesanan sedang diproses...' };
    }
    isSubmittingOrderRef.current = true;
    setIsSubmitting(true);
    console.log('[ORDER UI] submit started');

    try {
      const normalizedPaymentMethod = String(details.paymentMethod || '').toUpperCase().trim();
      if (normalizedPaymentMethod !== 'QRIS' && normalizedPaymentMethod !== 'DANA') {
        setIsSubmitting(false);
        isSubmittingOrderRef.current = false;
        return {
          success: false,
          error: 'Metode pembayaran tunai sudah tidak tersedia. Silakan gunakan QRIS atau DANA.',
        };
      }

      const rawSubtotal = cart.reduce((acc, item) => {
        const unit = calculateItemUnitPrice(item.product.price, item.size, item.topping, item.syrup, item.customOptions);
        return acc + unit * item.quantity;
      }, 0);
      const appliedDiscount = Math.min(rawSubtotal, Math.max(0, Number(details.discountAmount || 0)));
      const deliveryFee = details.orderType === 'DELIVERY' ? Math.max(0, Number(details.deliveryFee || 0)) : 0;
      const serviceFee = rawSubtotal > 0 ? FIXED_SERVICE_FEE : 0;
      const totalAmount = Math.max(0, rawSubtotal - appliedDiscount) + deliveryFee + serviceFee;

      const checkoutFingerprint = JSON.stringify({
        outletId: selectedOutlet.id,
        orderType: details.orderType,
        tableNumber: details.orderType === 'DINE IN' ? (details.tableNumber || '').trim() : '',
        customerName: (details.customerName || '').trim().toLowerCase(),
        customerPhone: (details.customerPhone || '').trim(),
        paymentMethod: details.paymentMethod,
        paymentReceiptPath: details.paymentReceiptPath || details.paymentReceiptUrl || '',
        pickupTime: details.pickupTime || '',
        voucherId: details.voucherId || '',
        voucherCode: details.voucherCode || '',
        discountAmount: appliedDiscount,
        deliveryAddress: details.orderType === 'DELIVERY' ? (details.deliveryAddress || '').trim() : '',
        customerLat: details.orderType === 'DELIVERY' ? (details.customerLat ?? null) : null,
        customerLng: details.orderType === 'DELIVERY' ? (details.customerLng ?? null) : null,
        deliveryDistance: details.orderType === 'DELIVERY' ? (details.deliveryDistance ?? null) : null,
        deliveryFee,
        serviceFee,
        generalNote: (generalNote || '').trim(),
        items: cart.map((item) => ({
          productId: item.product.id,
          quantity: item.quantity,
          size: item.size?.name || '',
          toppingId: item.topping?.id || '',
          syrupId: item.syrup?.id || '',
          customOptions: (item.customOptions || []).map((opt) => ({
            groupId: opt.groupId,
            optionId: opt.optionId,
            price: opt.price,
          })),
          note: (item.note || '').trim(),
        })),
      });

      if (!pendingOrderDraftRef.current || pendingOrderDraftRef.current.fingerprint !== checkoutFingerprint) {
        pendingOrderDraftRef.current = {
          fingerprint: checkoutFingerprint,
          id: `order-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          orderNumber: generateOrderNumber(),
        };
      }

      const { id: draftOrderId, orderNumber } = pendingOrderDraftRef.current;

      // Determine customerId based on logged-in member or "Jadi Member" choice
      let resolvedCustomerId: string | undefined = customerProfile?.id || customerProfile?.userId || undefined;

      if (!resolvedCustomerId && details.isMemberChoice !== false) {
        // Customer selected "Jadi Member" - save/link in Supabase existing customers table
        const member = await findOrCreateCustomerMember(details.customerName, details.customerPhone);
        if (member && member.id) {
          resolvedCustomerId = member.id;
        }
      }

      // Initial payment status based on payment method (only DANA and QRIS allowed for new orders)
      const initialPaymentStatus = details.paymentMethod === 'DANA'
        ? 'WAITING PAYMENT'
        : 'WAITING VERIFICATION';

      const newOrder: CustomerOrder = {
        id: draftOrderId,
        orderNumber,
        outletId: selectedOutlet.id,
        outletName: selectedOutlet.name,
        customerName: details.customerName,
        customerPhone: details.customerPhone,
        customerId: resolvedCustomerId,
        orderType: details.orderType,
        tableNumber: details.orderType === 'DINE IN' ? details.tableNumber : undefined,
        items: cart.map((item) => {
          const size = item.size || DEFAULT_SIZE;
          const isDummyTopping = !item.topping || item.topping.id === 'top-no-topping' || item.topping.name === 'Tanpa Topping' || item.topping.name === 'No Topping';
          const isDummySyrup = !item.syrup || item.syrup.id === 'syr-no-syrup' || item.syrup.name === 'Normal (Standard Recipe)' || item.syrup.name === 'No Syrup' || item.syrup.name === 'Normal';

          const toppingObj = !isDummyTopping ? item.topping : undefined;
          const syrupObj = !isDummySyrup ? item.syrup : undefined;
          const sizeObj = item.product.hasSize !== false && size ? { name: size.name, price: size.price } : undefined;

          const unitPrice = calculateItemUnitPrice(item.product.price, sizeObj, toppingObj, syrupObj, item.customOptions);
          return {
            id: `item-${Date.now()}-${item.product.id}-${Math.random().toString(36).slice(2, 6)}`,
            productId: item.product.id,
            name: item.product.name,
            price: item.product.price,
            unitPrice: unitPrice,
            quantity: item.quantity,
            image: item.product.image,
            note: item.note,
            size: sizeObj,
            topping: toppingObj ? { id: toppingObj.id, name: toppingObj.name, price: toppingObj.price } : undefined,
            syrup: syrupObj ? { id: syrupObj.id, name: syrupObj.name, price: syrupObj.price } : undefined,
            customOptions: item.customOptions,
          };
        }),
        totalAmount,
        paymentMethod: details.paymentMethod,
        paymentStatus: initialPaymentStatus,
        paymentReceiptUrl: details.paymentReceiptUrl,
        paymentReceiptPath: details.paymentReceiptPath,
        orderStatus: 'NEW',
        customerNote: generalNote
          ? (initialPromoItem ? `[PROMO: ${initialPromoItem.title}] ${generalNote}` : generalNote)
          : (initialPromoItem ? `[PROMO: ${initialPromoItem.title}]` : undefined),
        voucherId: details.voucherId || undefined,
        voucherCode: details.voucherCode || undefined,
        discountAmount: appliedDiscount > 0 ? appliedDiscount : undefined,
        pickupTime: details.pickupTime || undefined,
        pickup_time: details.pickupTime || undefined,
        deliveryAddress: details.orderType === 'DELIVERY' ? details.deliveryAddress : undefined,
        delivery_address: details.orderType === 'DELIVERY' ? details.deliveryAddress : undefined,
        customerLat: details.orderType === 'DELIVERY' ? details.customerLat : undefined,
        customer_lat: details.orderType === 'DELIVERY' ? details.customerLat : undefined,
        customerLng: details.orderType === 'DELIVERY' ? details.customerLng : undefined,
        customer_lng: details.orderType === 'DELIVERY' ? details.customerLng : undefined,
        deliveryDistance: details.orderType === 'DELIVERY' ? details.deliveryDistance : undefined,
        delivery_distance: details.orderType === 'DELIVERY' ? details.deliveryDistance : undefined,
        deliveryFee: details.orderType === 'DELIVERY' ? deliveryFee : undefined,
        delivery_fee: details.orderType === 'DELIVERY' ? deliveryFee : undefined,
        serviceFee,
        service_fee: serviceFee,
        createdAt: new Date().toISOString(),
      };

      console.log('[ORDER UI] createNewOrder started');
      const result = await createNewOrder(newOrder);
      if (result.success) {
        pendingOrderDraftRef.current = null;
        console.log('[ORDER FLOW] createNewOrder SUCCESS');
        console.log('[ORDER FLOW] confirmation transition START');
        console.log('[ORDER FLOW] confirmation order:', result.order?.orderNumber);

        // Retrieve and log the updated loyalty balance from Supabase (non-blocking)
        if (newOrder.customerId) {
          getCustomerLoyalty(newOrder.customerId)
            .then((updatedLoyalty) => {
              console.log('[Loyalty Balance Synced]: New balance is:', updatedLoyalty.pointsBalance);
            })
            .catch((loyaltyErr) => {
              console.error('[Loyalty Earning Sync Error]: Failed to refresh loyalty balance from Supabase:', loyaltyErr);
            });
        } else {
          console.warn('[Loyalty Earning Note]: Order tidak terhubung to customer.');
        }

        const finalCreatedOrder = result.order || newOrder;
        setIsSubmitting(false);
        setCompletedOrder(finalCreatedOrder);
        if (details.paymentMethod === 'DANA') {
          try {
            const serialized = JSON.stringify(finalCreatedOrder);
            sessionStorage.setItem('leton_dana_pending_order', serialized);
            localStorage.setItem('leton_dana_pending_order', serialized);
          } catch {}
        } else {
          setCart([]); // Clear cart
          localStorage.removeItem('leton_ordering_cart');
          setCurrentStep('confirmation');
        }
        scrollToTop();
        console.log('[ORDER UI] confirmation transition completed');
        return { success: true, order: finalCreatedOrder };
      } else {
        setIsSubmitting(false);
        const errMsg = result.error || 'Gagal menyimpan pesanan. Silakan coba beberapa saat lagi.';
        try {
          if (typeof window !== 'undefined' && !window.frameElement) {
            alert(errMsg);
          }
        } catch {}
        return { success: false, error: errMsg };
      }
    } catch (err: any) {
      console.error('Submit order error:', err);
      setIsSubmitting(false);
      const errMsg = err?.message || 'Terjadi kesalahan sistem saat memproses pesanan. Silakan coba lagi.';
      return { success: false, error: errMsg };
    } finally {
      isSubmittingOrderRef.current = false;
    }
  };

  const handleOrderAgain = () => {
    pendingOrderDraftRef.current = null;
    setCompletedOrder(null);
    scrollToTop();
    setCurrentStep('menu');
  };

  const isOutletStepActive =
    currentStep === 'outlet' ||
    (!selectedOutlet &&
      currentStep !== 'profile' &&
      currentStep !== 'confirmation' &&
      currentStep !== 'member_choice');

  const isQrOutletFlow =
    !isMemberOnlyFlow &&
    Boolean(
      resolveQrOutlet(
        initialOutletId || getOutletIdFromUrl(),
        data?.branches,
        data?.contactSettings?.whatsapp
      )
    );

  const handleBackFromOutlet = () => {
    if (initialPromoItem) {
      if (onBackToPromoDetail) {
        onBackToPromoDetail();
      } else {
        onClose();
      }
      return;
    }
    if (!customerProfile && !isQrOutletFlow) {
      setCurrentStep('member_choice');
      setMemberChoiceStep('choice');
    } else {
      onClose();
    }
  };

  return (
    <div
      ref={modalContainerRef}
      data-scroll-container="true"
      className="fixed inset-0 z-50 overflow-y-auto flex flex-col font-sans animate-fadeIn bg-[#F8FAFC] text-[#0A192F]"
    >
      {/* Show top CustomerHeader only on the member_choice step; Outlet, Menu, Checkout, Confirmation, and Profile have their own UX Pilot sticky headers */}
      {currentStep === 'member_choice' && !isOutletStepActive && (
        <CustomerHeader
          showBack={true}
          onBack={() => {
            if (memberChoiceStep !== 'choice') {
              setMemberChoiceStep('choice');
            } else {
              onClose();
            }
          }}
          showProfile={true}
          isProfileActive={false}
          profileName={customerProfile?.namaLengkap}
          onOpenProfile={() => {}}
          showCart={false}
          cartCount={cart.reduce((a, b) => a + b.quantity, 0)}
          onOpenCart={() => setIsCartOpen(true)}
          showClose={true}
          onClose={onClose}
        />
      )}

      {/* Main Step Body */}
      <main className="flex-1 w-full min-h-screen bg-[#F8FAFC] text-[#0A192F]">
        <ErrorBoundary
          fallbackTitle="Terjadi Kendala pada Pemesanan"
          fallbackMessage="Silakan klik tombol di bawah untuk kembali ke langkah pemilihan outlet."
          onReset={() => setCurrentStep('outlet')}
        >
          {checkingAuth ? (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
              <div className="w-10 h-10 border-4 border-[#0056D2] border-t-transparent rounded-full animate-spin"></div>
              <p className="text-[#64748B] font-semibold text-xs tracking-wider uppercase">
                Loading Member Session...
              </p>
            </div>
          ) : (
            <>
              {/* Step 0: Halaman Member (Daftar, Login, Lanjut Tanpa Member) */}
              {currentStep === 'member_choice' && (
                <div className="w-full min-h-screen sm:max-w-md sm:mx-auto sm:px-4 sm:py-10 animate-fadeIn bg-white sm:bg-transparent">
                  {memberChoiceStep === 'choice' && (
                    <div className="w-full bg-white sm:rounded-[2rem] sm:shadow-[0_20px_50px_rgba(0,86,210,0.12)] sm:border sm:border-slate-100 overflow-hidden">
                      {/* Top Blue Gradient Header with Brand Logo & Wave/Cloud Transition */}
                      <div className="relative bg-gradient-to-br from-[#0066FF] via-[#0052CC] to-[#003A99] text-white pt-6 pb-10 px-6 text-center overflow-hidden">
                        {/* Decorative Ambient Circles */}
                        <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/10 blur-2xl pointer-events-none" />
                        <div className="absolute top-10 -left-12 w-36 h-36 rounded-full bg-sky-300/15 blur-2xl pointer-events-none" />
                        <div className="absolute bottom-6 right-1/4 w-24 h-24 rounded-full bg-white/5 blur-xl pointer-events-none" />

                        {/* Optional Skip Top Bar (Back/Close handled by sticky CustomerHeader above) */}
                        {!isMemberOnlyFlow && (
                          <div className="relative z-10 flex items-center justify-end mb-3">
                            <button
                              type="button"
                              onClick={() => {
                                setCustomerProfile(null);
                                if (isQrOutletFlow && selectedOutlet) {
                                  setCurrentStep('menu');
                                } else {
                                  setCurrentStep('outlet');
                                }
                              }}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-xs text-xs font-semibold text-white transition-all cursor-pointer"
                            >
                              <span>Tanpa Member</span>
                              <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}

                        {/* Brand Logo & LetonKu Name */}
                        <div className="relative z-10 flex flex-col items-center">
                          <div className="w-20 h-20 rounded-full bg-white/15 backdrop-blur-md p-1.5 shadow-lg border border-white/25 mb-3 flex items-center justify-center">
                            <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden shadow-inner">
                              {!logoImgError ? (
                                <img
                                  src={cmsLogoSrc}
                                  alt={siteSettings?.brandName || 'LetonKu Logo'}
                                  decoding="async"
                                  referrerPolicy="no-referrer"
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    const target = e.currentTarget;
                                    if (!target.src.endsWith('/logo_icon.jpg')) {
                                      target.src = '/logo_icon.jpg';
                                    } else {
                                      setLogoImgError(true);
                                    }
                                  }}
                                />
                              ) : (
                                <Coffee className="w-8 h-8 text-[#0052CC]" />
                              )}
                            </div>
                          </div>

                          <p className="text-xs font-medium text-blue-100 tracking-wide">
                            Selamat Datang di
                          </p>
                          <h1 className="text-2xl font-extrabold tracking-tight text-white mt-0.5">
                            LetonKu
                          </h1>
                        </div>

                        {/* White Wave / Cloud Transition at Bottom of Header */}
                        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none pointer-events-none">
                          <svg
                            className="relative block w-full h-10 sm:h-12"
                            viewBox="0 0 1440 160"
                            preserveAspectRatio="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M0,64 C240,140 480,0 720,64 C960,128 1200,20 1440,80 L1440,160 L0,160 Z"
                              fill="rgba(255,255,255,0.22)"
                            />
                            <path
                              d="M0,96 C320,20 640,148 960,72 C1180,20 1320,88 1440,64 L1440,160 L0,160 Z"
                              fill="rgba(255,255,255,0.45)"
                            />
                            <path
                              d="M0,112 C360,168 720,32 1080,112 C1260,148 1360,96 1440,112 L1440,160 L0,160 Z"
                              fill="#FFFFFF"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* White Body with Options */}
                      <div className="px-6 sm:px-8 pt-3 pb-8 bg-white">
                        <div className="text-center mb-5">
                          <span className="inline-block px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-[#0056D2] text-[11px] font-bold tracking-wider uppercase mb-1.5">
                            {isMemberOnlyFlow ? 'LetonKu Membership' : 'Online Ordering Access'}
                          </span>
                          <h2 className="font-extrabold text-xl text-[#0A192F] tracking-tight">
                            {isMemberOnlyFlow ? 'Member Sign In / Sign Up' : 'Pilih Cara Masuk'}
                          </h2>
                          <p className="mt-1 text-[#64748B] text-xs max-w-sm mx-auto leading-relaxed">
                            {isMemberOnlyFlow
                              ? 'Sign in or create a member account to view your orders, profile, and loyalty points.'
                              : 'Pilih opsi di bawah untuk bergabung sebagai member atau lanjut sebagai tamu.'}
                          </p>
                        </div>

                        <div className="space-y-3">
                          {/* 1. DAFTAR MEMBER */}
                          <button
                            type="button"
                            onClick={() => setMemberChoiceStep('register')}
                            className="w-full p-4 rounded-2xl bg-white border-2 border-[#0056D2] hover:bg-blue-50/30 transition-all text-left flex items-center justify-between group cursor-pointer shadow-xs"
                          >
                            <div className="flex items-center gap-3.5">
                              <div className="w-12 h-12 rounded-xl bg-[#0056D2] text-white flex items-center justify-center shrink-0 shadow-xs">
                                <UserPlus className="w-6 h-6" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-sm text-[#0A192F]">
                                    Join Leton Member
                                  </span>
                                  <span className="px-2 py-0.5 rounded-md bg-blue-50 text-[#0056D2] text-[10px] font-bold uppercase">
                                    Recommended
                                  </span>
                                </div>
                                <p className="text-[#64748B] text-xs mt-0.5">
                                  Create an account to earn loyalty points &amp; exclusive rewards.
                                </p>
                              </div>
                            </div>
                            <ArrowRight className="w-5 h-5 text-[#0056D2] group-hover:translate-x-1 transition-transform shrink-0" />
                          </button>

                          {/* 2. LOGIN MEMBER */}
                          <button
                            type="button"
                            onClick={() => setMemberChoiceStep('login')}
                            className="w-full p-4 rounded-2xl bg-white border border-[#E2E8F0] hover:border-[#0056D2] transition-all text-left flex items-center justify-between group cursor-pointer shadow-2xs"
                          >
                            <div className="flex items-center gap-3.5">
                              <div className="w-12 h-12 rounded-xl bg-slate-100 text-[#0A192F] flex items-center justify-center shrink-0 group-hover:bg-[#0A192F] group-hover:text-white transition-all">
                                <LogIn className="w-6 h-6" />
                              </div>
                              <div>
                                <span className="font-bold text-sm text-[#0A192F] block">
                                  Member Sign In
                                </span>
                                <p className="text-[#64748B] text-xs mt-0.5">
                                  Already have a LetonKu member account? Sign in here.
                                </p>
                              </div>
                            </div>
                            <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-[#0056D2] group-hover:translate-x-1 transition-all shrink-0" />
                          </button>

                          {/* 3. LANJUT TANPA MEMBER */}
                          {!isMemberOnlyFlow && (
                            <button
                              type="button"
                              onClick={() => {
                                setCustomerProfile(null);
                                if (isQrOutletFlow && selectedOutlet) {
                                  setCurrentStep('menu');
                                } else {
                                  setCurrentStep('outlet');
                                }
                              }}
                              className="w-full p-4 rounded-2xl bg-slate-50 border border-[#E2E8F0] hover:bg-slate-100 transition-all text-left flex items-center justify-between group cursor-pointer"
                            >
                              <div className="flex items-center gap-3.5">
                                <div className="w-12 h-12 rounded-xl bg-white border border-[#E2E8F0] text-[#64748B] flex items-center justify-center shrink-0">
                                  <ShoppingBag className="w-6 h-6" />
                                </div>
                                <div>
                                  <span className="font-bold text-sm text-[#0A192F] block">
                                    Continue as Guest
                                  </span>
                                  <p className="text-[#64748B] text-xs mt-0.5">
                                    Order directly without an account (No loyalty points).
                                  </p>
                                </div>
                              </div>
                              <ArrowRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform shrink-0" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {memberChoiceStep === 'register' && (
                    <CustomerAuthForm
                      initialMode="register"
                      onSkipWithoutMember={
                        !isMemberOnlyFlow
                          ? () => {
                              setCustomerProfile(null);
                              if (isQrOutletFlow && selectedOutlet) {
                                setCurrentStep('menu');
                              } else {
                                setCurrentStep('outlet');
                              }
                            }
                          : undefined
                      }
                      onAuthSuccess={(profile) => {
                        setCustomerProfile(profile);
                        if (isMemberOnlyFlow) {
                          setCurrentStep('profile');
                        } else if (isQrOutletFlow && selectedOutlet) {
                          setCurrentStep('menu');
                        } else {
                          setCurrentStep('outlet');
                        }
                      }}
                    />
                  )}

                  {memberChoiceStep === 'login' && (
                    <CustomerAuthForm
                      initialMode="login"
                      onSkipWithoutMember={
                        !isMemberOnlyFlow
                          ? () => {
                              setCustomerProfile(null);
                              if (isQrOutletFlow && selectedOutlet) {
                                setCurrentStep('menu');
                              } else {
                                setCurrentStep('outlet');
                              }
                            }
                          : undefined
                      }
                      onAuthSuccess={(profile) => {
                        setCustomerProfile(profile);
                        if (isMemberOnlyFlow) {
                          setCurrentStep('profile');
                        } else if (isQrOutletFlow && selectedOutlet) {
                          setCurrentStep('menu');
                        } else {
                          setCurrentStep('outlet');
                        }
                      }}
                    />
                  )}
                </div>
              )}

              {/* Step 1: Pilih Outlet (also default fallback if no outlet selected) */}
              {isOutletStepActive && (
                <OutletSelector
                  onSelectOutlet={(outlet) => {
                    setSelectedOutlet(outlet);
                    if (initialPromoItem) {
                      const catalog =
                        Array.isArray(data?.menuItems) && data.menuItems.length > 0
                          ? data.menuItems
                          : initialLetonData.menuItems;
                      const matchedProduct = resolvePromoCatalogProduct(
                        initialPromoItem,
                        catalog,
                        outlet.id
                      );
                      if (matchedProduct) {
                        const promoCartItem = buildPromoCartItem(initialPromoItem, matchedProduct);
                        setCart([promoCartItem]);
                        try {
                          localStorage.setItem('leton_ordering_cart', JSON.stringify([promoCartItem]));
                        } catch {}
                      }
                      // PROMO FLOW: Directly to Checkout, bypass Menu completely!
                      setCurrentStep('checkout');
                    } else {
                      setCurrentStep('menu');
                    }
                  }}
                  onClose={handleBackFromOutlet}
                  orderType={activeOrderType}
                />
              )}

              {/* Step 2: Menu */}
              {currentStep === 'menu' && selectedOutlet && (
                <OrderMenu
                  outlet={selectedOutlet}
                  cart={cart}
                  onAddToCart={handleAddToCart}
                  onUpdateCartQuantity={handleUpdateQuantity}
                  onOpenCart={() => setIsCartOpen(true)}
                  onChangeOutlet={() => setCurrentStep('outlet')}
                  onBack={() => setCurrentStep('outlet')}
                  preSelectedProduct={pendingCustomizeItem}
                  onClearPreSelectedProduct={() => setPendingCustomizeItem(null)}
                />
              )}

              {/* Step 3: Checkout */}
              {currentStep === 'checkout' && selectedOutlet && (
                <OrderCheckout
                  outlet={selectedOutlet}
                  cart={cart}
                  generalNote={generalNote}
                  onBackToCart={() => {
                    if (initialPromoItem) {
                      // PROMO FLOW: Back button returns cleanly to outlet selection!
                      setCurrentStep('outlet');
                    } else {
                      setIsCartOpen(true);
                    }
                  }}
                  onSubmitOrder={handleSubmitOrder}
                  isSubmitting={isSubmitting}
                  customerProfile={customerProfile}
                  initialOrderType={activeOrderType}
                  activePromo={initialPromoItem}
                  onClearCart={() => {
                    setCart([]);
                    localStorage.removeItem('leton_ordering_cart');
                  }}
                />
              )}

              {/* Step: Profile / Auth */}
              {currentStep === 'profile' && (
                <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
                  {customerProfile ? (
                    <CustomerProfileTab
                      profile={customerProfile}
                      onBack={onClose}
                      onStartOrder={() => {
                        setSelectedOutlet(null);
                        setCurrentStep('outlet');
                      }}
                      onLogout={async () => {
                        try {
                          await logoutCustomer();
                        } catch (err) {
                          console.error('[Customer Logout Error]:', err);
                        } finally {
                          setCustomerProfile(null);
                          setCurrentStep('member_choice');
                          setMemberChoiceStep('choice');
                        }
                      }}
                      onProfileUpdate={(updated) => {
                        setCustomerProfile((prev) => (prev ? { ...prev, ...updated } : null));
                      }}
                    />
                  ) : (
                    <div className="py-6">
                      <CustomerAuthForm
                        onBackToChoice={onClose}
                        onAuthSuccess={(profile) => {
                          setCustomerProfile(profile);
                          setCurrentStep('profile');
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Step: Confirmation */}
              {currentStep === 'confirmation' && completedOrder && (
                <OrderConfirmation
                  order={completedOrder}
                  outlet={selectedOutlet || ({ id: completedOrder.outletId, name: completedOrder.outletName || 'LetonKu' } as any)}
                  onOrderAgain={handleOrderAgain}
                  onBackToHome={onClose}
                />
              )}
            </>
          )}
        </ErrorBoundary>
      </main>

      {/* Cart Drawer */}
      <AnimatePresence>
        {isCartOpen && selectedOutlet && (
          <OrderCartDrawer
            outlet={selectedOutlet}
            cart={cart}
            onUpdateQuantity={handleUpdateQuantity}
            onRemoveItem={handleRemoveItem}
            onUpdateNote={handleUpdateNote}
            generalNote={generalNote}
            onUpdateGeneralNote={setGeneralNote}
            onClose={() => setIsCartOpen(false)}
            onContinue={() => {
              setIsCartOpen(false);
              setCurrentStep('checkout');
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
